import { useTranslation } from 'react-i18next'

import { CompareSegmentsGroup } from '@/components/quick-study-results/CompareSegmentsPicker'
import { maxCutsFor } from '@/components/quick-study-results/divergingStacked'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

import { TwinTestCutSuggestions } from './TwinTestCutSuggestions'
import {
  cutsForSegmentBy,
  segmentByFromSelectedCuts,
  twinTestCutOptionTestId,
} from './twinTestCutMapping'

interface TwinTestCutPickerProps {
  groups: Array<CompareSegmentsGroup>
  selected: Array<string>
  onChange: (selected: Array<string>) => void
  maxCuts?: number
}

export function TwinTestCutPicker(props: TwinTestCutPickerProps) {
  const { groups, selected, onChange, maxCuts } = props

  const { t } = useTranslation('app')

  const resolvedMaxCuts = maxCuts ?? maxCutsFor(1)
  const selectedSet = new Set(selected)
  const atCap = selected.length >= resolvedMaxCuts
  const visibleGroups = groups.filter((group) => group.options.length > 0)
  const showGroupLabels = visibleGroups.length > 1

  function handleToggle(cutId: string) {
    if (selectedSet.has(cutId)) {
      onChange(selected.filter((id) => id !== cutId))
      return
    }
    if (atCap) {
      return
    }
    onChange([...selected, cutId])
  }

  function handleSuggestion(segmentBy: string) {
    onChange(cutsForSegmentBy(visibleGroups, segmentBy))
  }

  if (visibleGroups.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-2" data-testid="twin-test-cut-picker">
      <Text size="sm" weight="medium" as="label">
        {t('threads.detail.command.breakDownBy')}
      </Text>
      <TwinTestCutSuggestions
        groups={visibleGroups}
        selectedSegmentBy={segmentByFromSelectedCuts(selected)}
        onChange={handleSuggestion}
      />
      {visibleGroups.map((group) => (
        <div key={group.groupLabel} className="space-y-1">
          {showGroupLabels ? (
            <Text size="xs" color="muted" className="uppercase tracking-wider">
              {group.groupLabel}
            </Text>
          ) : null}
          {group.options.map((option) => {
            const isChecked = selectedSet.has(option.id)
            const isDisabled = !isChecked && atCap

            return (
              <label
                key={option.id}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-sm',
                  isDisabled && 'cursor-not-allowed opacity-40',
                )}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={() => handleToggle(option.id)}
                  className="h-3.5 w-3.5 accent-current"
                  data-testid={twinTestCutOptionTestId(option.id)}
                />
                <span className="min-w-0 flex-1 truncate" title={option.label}>
                  {option.label}
                </span>
                {typeof option.n === 'number' ? (
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    n={option.n}
                  </span>
                ) : null}
              </label>
            )
          })}
        </div>
      ))}
    </div>
  )
}
